import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Users, Star, TrendingUp, TrendingDown, Minus, BarChart3, Award,
  Loader2, User, Building, ArrowUpRight, ArrowDownRight, ChevronRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, PolarGrid, PolarAngleAxis, Radar
} from 'recharts';

const COLORS = ['#1B5AFF', '#3B82F6', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

const TeamComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await employerAPI.getTeamComparison();
      setData(res.data);
    } catch (error) {
      console.error('Error fetching team comparison:', error);
      toast.error('Failed to load team comparison data');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-[#5B6472]'}`}
          />
        ))}
      </div>
    );
  };

  const renderTrend = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-[#5B6472]" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data || data.team_members.length === 0) {
    return (
      <DashboardLayout>
        <div className="space-y-6" data-testid="team-comparison-page">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0F1E]">Team Comparison</h1>
            <p className="text-[#5B6472] mt-1">Compare performance across your team members</p>
          </div>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
            <Users className="w-16 h-16 text-[#5B6472] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-[#0A0F1E] mb-2">No Team Data Yet</h2>
            <p className="text-[#5B6472] mb-6">
              Create VETT records for your team members to see comparison analytics.
            </p>
            <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF]/90">
              Create First Record
            </Button>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const ratingDistData = Object.entries(data.rating_distribution)
    .map(([rating, count]) => ({ name: `${rating} Stars`, value: count, rating: parseInt(rating) }))
    .sort((a, b) => b.rating - a.rating);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="team-comparison-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Team Comparison</h1>
          <p className="text-[#5B6472] mt-1">Compare performance across your team members</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="total-members-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#1B5AFF]/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#1B5AFF]" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Team Members</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{data.overall_stats.total_members}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="avg-rating-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Star className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Avg Rating</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{data.overall_stats.avg_rating}</p>
              </div>
            </div>
          </Card>
          
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="total-records-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Total Records</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{data.overall_stats.total_records}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Rating Comparison Bar Chart */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">Rating Comparison</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.comparison_data} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
                  <XAxis type="number" domain={[0, 5]} tick={{ fill: '#5B6472' }} />
                  <YAxis dataKey="name" type="category" tick={{ fill: '#5B6472', fontSize: 12 }} width={80} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E8EDF5' }}
                    labelStyle={{ color: '#F5F5F7' }}
                  />
                  <Bar dataKey="rating" fill="#1B5AFF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Rating Distribution Pie Chart */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">Rating Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={ratingDistData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  >
                    {ratingDistData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E8EDF5' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Top Performers */}
        {data.top_performers.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="top-performers-card">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-yellow-400" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Top Performers</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.top_performers.map((performer, index) => (
                <div
                  key={performer.employee_id}
                  className={`p-4 rounded-xl border ${
                    index === 0 ? 'bg-yellow-500/10 border-yellow-500/30' :
                    index === 1 ? 'bg-[#5B6472]/10 border-[#5B6472]/30' :
                    'bg-amber-600/10 border-amber-600/30'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500 text-black' :
                      index === 1 ? 'bg-[#5B6472] text-[#0A0F1E]' :
                      'bg-amber-600 text-[#0A0F1E]'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#0A0F1E] font-medium truncate">{performer.name}</p>
                      <p className="text-[#5B6472] text-sm truncate">{performer.job_title}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    {renderStars(performer.avg_rating)}
                    <span className="text-[#0A0F1E] font-medium">{performer.avg_rating}</span>
                  </div>
                  {performer.top_strengths.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {performer.top_strengths.slice(0, 2).map(strength => (
                        <span key={strength} className="px-2 py-0.5 bg-[#F5F8FF] text-[#5B6472] rounded text-xs">
                          {strength}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Department Breakdown */}
        {data.department_breakdown.length > 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Department Performance</h3>
            </div>
            <div className="space-y-3">
              {data.department_breakdown.map(dept => (
                <div key={dept.department} className="flex items-center justify-between p-3 rounded-lg bg-[#F5F8FF]">
                  <div className="flex items-center gap-3">
                    <span className="text-[#0A0F1E] font-medium">{dept.department}</span>
                    <span className="text-[#5B6472] text-sm">({dept.member_count} members)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {renderStars(dept.avg_rating)}
                    <span className="text-[#0A0F1E] font-medium ml-2">{dept.avg_rating}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Team Members Table */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">All Team Members</h3>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="team-members-table">
              <thead>
                <tr className="border-b border-[#E8EDF5]">
                  <th className="text-left py-3 px-4 text-[#5B6472] font-medium">Employee</th>
                  <th className="text-left py-3 px-4 text-[#5B6472] font-medium">Department</th>
                  <th className="text-center py-3 px-4 text-[#5B6472] font-medium">Rating</th>
                  <th className="text-center py-3 px-4 text-[#5B6472] font-medium">Trend</th>
                  <th className="text-center py-3 px-4 text-[#5B6472] font-medium">Records</th>
                  <th className="text-left py-3 px-4 text-[#5B6472] font-medium">Top Strengths</th>
                </tr>
              </thead>
              <tbody>
                {data.team_members.map(member => (
                  <tr key={member.employee_id} className="border-b border-[#E8EDF5]/50 hover:bg-[#F5F8FF]/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <User className="w-4 h-4 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium">{member.name}</p>
                          <p className="text-[#5B6472] text-xs">{member.job_title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#5B6472]">
                      {member.departments.join(', ') || '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {renderStars(member.avg_rating)}
                        <span className="text-[#0A0F1E]">{member.avg_rating}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-center">
                        {renderTrend(member.rating_trend)}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-[#0A0F1E]">
                      {member.record_count}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {member.top_strengths.slice(0, 3).map(strength => (
                          <span key={strength} className="px-2 py-0.5 bg-[#1B5AFF]/20 text-[#1B5AFF] rounded text-xs">
                            {strength}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TeamComparison;
